import { useEffect, useState } from 'react';
import { purchasingAPI } from '../../../lib/api';
import { FileSignature } from 'lucide-react';
import type { FilterState } from '../dashboardFilterUtils';
import { CONTRACT_STATUS_OPTIONS } from '../costTabFilters';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../filters/FilterSection';

interface ContractSectionProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
}

/**
 * CR-5454: CONTRACT group — the Cost tab's Contract Status, Part Number and
 * Contract Type filters. Rendered on the Cost tab only.
 *
 * Contract Type lists type NAMES: types are configured per company, so at group
 * or division level the same "Standard" exists once per site and the filter
 * matches them all (the backend compares by name too).
 */
export function ContractSection({ filters, setFilters }: ContractSectionProps) {
  const [contractTypes, setContractTypes] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    purchasingAPI.getContractTypes({ page_size: 200 })
      .then((res) => {
        if (cancelled) return;
        const names = (res.data.results || []).map((t) => t.name).filter(Boolean);
        setContractTypes(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      })
      .catch((err: unknown) => {
        // The filter still works without the list — it just offers "All".
        console.error('Failed to load contract types:', err);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <FilterSection title="Contract" icon={FileSignature}>
      <FilterGrid>
        <FilterField label="Contract status">
          <select
            value={filters.contractStatus || ''}
            onChange={(e) => setFilters({ ...filters, contractStatus: e.target.value })}
            className={filterControlClass}
            aria-label="Contract status"
          >
            {CONTRACT_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Part number">
          <input
            type="search"
            value={filters.partNumber || ''}
            onChange={(e) => setFilters({ ...filters, partNumber: e.target.value })}
            placeholder="Search part number..."
            className={filterControlClass}
            aria-label="Part number"
          />
        </FilterField>

        <FilterField label="Contract type">
          <select
            value={filters.contractType || ''}
            onChange={(e) => setFilters({ ...filters, contractType: e.target.value })}
            className={filterControlClass}
            aria-label="Contract type"
          >
            <option value="">All contract types</option>
            {contractTypes.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </FilterField>
      </FilterGrid>
    </FilterSection>
  );
}
